import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  BOOKS_PAGE_SIZE,
  createBook,
  deleteBook,
  getBook,
  getBookAuthors,
  getBookTitles,
  getPublishedBooks,
  updateBook,
} from '../services/book_service'
import './BookPublishe.css'

const currency = new Intl.NumberFormat('en-GH', {
  style: 'currency',
  currency: 'GHS',
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 2,
})

const getBookId = (book) => book.bookid ?? book.book_id ?? book.id ?? book.isbn
const getIsbn = (book) => book.isbn ?? book.book_isbn ?? book.bookIsbn ?? '-'
const getTitle = (book) =>
  book.title ?? book.book_title ?? book.bookTitle ?? book.name ?? '-'
const getPrice = (book) =>
  Number(
    book.unitprice ?? book.price ?? book.book_price ?? book.unit_price ?? 0,
  )
const getStock = (book) =>
  Number(book.instock ?? book.in_stock ?? book.stock ?? book.quantity ?? 0)
const getStockValue = (book) => {
  const value = book.stockvalue ?? book.stock_value ?? book.stockValue
  return value === undefined || value === null
    ? getPrice(book) * getStock(book)
    : Number(value)
}
const getStockState = (stock) => {
  if (stock <= 0) return 'out'
  if (stock <= 10) return 'low'
  return 'in'
}

const emptyForm = {
  isbn: '',
  book_title: '',
  unitprice: '',
  authorids: [],
}
const toBookForm = (book) => ({
  bookid: getBookId(book),
  isbn: book.isbn ?? '',
  book_title: book.book_title ?? '',
  unitprice: book.unitprice ?? '',
  authorids: (book.authorids ?? []).map(String),
})

function BookModal({
  mode,
  book,
  authors,
  showAuthors,
  showIsbn,
  onClose,
  onSave,
  isSaving,
}) {
  const isDelete = mode === 'delete'
  const isEdit = mode === 'edit'
  const [form, setForm] = useState(book ? toBookForm(book) : emptyForm)

  const updateField = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }))
  const submit = (event) => {
    event.preventDefault()
    if (isEdit) {
      const { unitprice, ...rest } = form
      onSave(rest)
      return
    }
    onSave(form)
  }

  return (
    <div
      className='book-modal__backdrop'
      role='presentation'
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className='book-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='book-modal-title'
      >
        <div className='book-modal__header'>
          <div>
            <span className='app-content__eyebrow'>
              {isDelete ? 'Confirm action' : 'Book catalogue'}
            </span>
            <h2 id='book-modal-title'>
              {isDelete
                ? 'Delete book?'
                : isEdit
                  ? 'Edit book'
                  : 'Add new book'}
            </h2>
          </div>
          <button
            className='book-modal__close'
            type='button'
            onClick={onClose}
            aria-label='Close modal'
          >
            x
          </button>
        </div>
        {isDelete ? (
          <>
            <p className='book-modal__message'>
              This will permanently remove <strong>{getTitle(book)}</strong>{' '}
              {showIsbn ? `(${getIsbn(book)}) ` : ''}from the book list.
            </p>
            <div className='book-modal__actions'>
              <button
                className='book-modal__cancel'
                type='button'
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className='book-modal__danger'
                type='button'
                onClick={() => onSave(book)}
                disabled={isSaving}
              >
                {isSaving ? 'Deleting...' : 'Delete book'}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className='book-form'>
              {showIsbn && (
                <label>
                  Book ISBN
                  <input
                    name='isbn'
                    value={form.isbn}
                    onChange={updateField}
                    required
                  />
                </label>
              )}
              <label>
                Book title
                <input
                  name='book_title'
                  value={form.book_title}
                  onChange={updateField}
                  required
                />
              </label>
              {!isEdit && (
                <label>
                  Unit price
                  <input
                    name='unitprice'
                    type='number'
                    min='0'
                    step='0.01'
                    value={form.unitprice}
                    onChange={updateField}
                    required
                  />
                </label>
              )}
              {showAuthors && (
                <label className='book-form__authors'>
                  Authors
                  <select
                    name='authorids'
                    multiple
                    size='5'
                    value={form.authorids}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        authorids: Array.from(
                          event.target.selectedOptions,
                          (option) => option.value,
                        ),
                      }))
                    }
                  >
                    {authors.map((author) => (
                      <option
                        key={author.authorid}
                        value={String(author.authorid)}
                      >
                        {author.author_name}
                      </option>
                    ))}
                  </select>
                  <small>Hold Ctrl (or Cmd) to select multiple authors.</small>
                </label>
              )}
            </div>
            <div className='book-modal__actions'>
              <button
                className='book-modal__cancel'
                type='button'
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className='book-modal__save'
                type='submit'
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : isEdit ? 'Save changes' : 'Add book'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  )
}

function BookPublishe({
  bookType = 'published_book',
  heading = 'Published books',
  breadcrumb = 'Publish Books',
  listTitle = 'Published book list',
  basePath = '/admin/books/publish-books',
  showAuthors = true,
  showRank = true,
  showIsbn = true,
}) {
  const { user: authUser, hasPermission } = useAuth()
  const canAddBook = hasPermission('addBook')
  const canEditBook = hasPermission('editBook')
  const canDeleteBook = hasPermission('deleteBook')
  const [books, setBooks] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [search, setSearch] = useState('')
  const [titleFilter, setTitleFilter] = useState('')
  const [titleOptions, setTitleOptions] = useState([])
  const [authorOptions, setAuthorOptions] = useState([])
  const [loadingBookId, setLoadingBookId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [modal, setModal] = useState(null)
  const [error, setError] = useState('')

  const loadBooks = async (
    cursor = null,
    page = 1,
    searchTerm = search,
    title = titleFilter,
    signal,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getPublishedBooks({
        cursor,
        page,
        search: searchTerm,
        title,
        type: bookType,
        signal,
      })
      if (signal?.aborted) return
      setBooks(result.books)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      if (requestError.name !== 'AbortError') setError(requestError.message)
    } finally {
      if (!signal?.aborted) setIsLoading(false)
    }
  }

  useEffect(() => {
    getBookTitles(bookType)
      .then(setTitleOptions)
      .catch((requestError) => setError(requestError.message))
    if (!showAuthors) return
    getBookAuthors()
      .then(setAuthorOptions)
      .catch((requestError) => setError(requestError.message))
  }, [bookType, showAuthors])

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(
      () => {
        setCursorHistory([])
        loadBooks(null, 1, search, titleFilter, controller.signal)
      },
      search ? 400 : 0,
    )

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, titleFilter])

  const rankOffset = cursorHistory.length * BOOKS_PAGE_SIZE
  const columnCount = 5 + (showRank ? 1 : 0) + (showIsbn ? 1 : 0)
  const pageStockValue = useMemo(
    () => books.reduce((total, book) => total + getStockValue(book), 0),
    [books],
  )

  const goToNextCursor = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadBooks(nextCursor, nextPage || currentPage + 1)
  }

  const goToPreviousCursor = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadBooks(previous.cursor, previous.page)
  }

  const openEditModal = async (book) => {
    const bookId = getBookId(book)
    setLoadingBookId(bookId)
    setError('')
    try {
      const loadedBook = await getBook(bookId)
      setModal({ mode: 'edit', book: { ...book, ...loadedBook } })
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoadingBookId(null)
    }
  }

  const saveBook = async (value) => {
    setIsSaving(true)
    setError('')
    try {
      const userid = authUser?.userid ?? authUser?.user_id ?? authUser?.id
      if (modal.mode === 'delete') await deleteBook(getBookId(value))
      else if (modal.mode === 'edit')
        await updateBook(value.bookid, {
          ...value,
          book_type: bookType,
          userid,
        })
      else await createBook({ ...value, book_type: bookType, userid })
      setModal(null)
      setCursorHistory([])
      await loadBooks(null, 1)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }
  return (
    <div className='book-page'>
      <nav className='book-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>{breadcrumb}</strong>
      </nav>
      <header className='book-page__header'>
        <div>
          <span className='app-content__eyebrow'>Catalogue</span>
          <h1>{heading}</h1>
          <p>Track titles, pricing, and stock value across the catalogue.</p>
        </div>
        {canAddBook && (
          <button
            className='book-page__add-button'
            type='button'
            onClick={() => setModal({ mode: 'add' })}
          >
            <span aria-hidden='true'>+</span> Add new book
          </button>
        )}
      </header>
      {error && (
        <div className='book-page__error' role='alert'>
          {error}
          <button
            type='button'
            onClick={() => loadBooks(currentCursor, currentPage)}
          >
            Retry
          </button>
        </div>
      )}
      <section className='book-table-card' aria-labelledby='book-list-title'>
        <div className='book-table-card__header'>
          <div>
            <h2 id='book-list-title'>{listTitle}</h2>
            <p>
              {books.length} books on this page - stock value{' '}
              {currency.format(pageStockValue)}
            </p>
          </div>
        </div>
        <div className='book-toolbar'>
          <label className='book-search'>
            <span aria-hidden='true'>search</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={
                showIsbn ? 'Search by ISBN or title' : 'Search by title'
              }
              aria-label='Search published books'
            />
          </label>
          <select
            className='book-filter'
            value={titleFilter}
            onChange={(event) => setTitleFilter(event.target.value)}
            aria-label='Filter by book title'
          >
            <option value=''>All book titles</option>
            {titleOptions.map((option) => {
              const value =
                typeof option === 'object' ? getTitle(option) : option
              return (
                <option key={value} value={value}>
                  {value}
                </option>
              )
            })}
          </select>
        </div>
        <div className='book-table-wrap'>
          <table className='book-table'>
            <thead>
              <tr>
                {showRank && <th>Rank</th>}
                {showIsbn && <th>Book ISBN</th>}
                <th>Book title</th>
                <th>Price</th>
                <th>In stock</th>
                <th>Stock value</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='book-table__empty' colSpan={columnCount}>
                    Loading books...
                  </td>
                </tr>
              ) : books.length ? (
                books.map((book, index) => {
                  const stock = getStock(book)
                  const bookId = getBookId(book)
                  return (
                    <tr key={bookId ?? index}>
                      {showRank && (
                        <td>{book.rank ?? rankOffset + index + 1}</td>
                      )}
                      {showIsbn && <td>{getIsbn(book)}</td>}
                      <td>
                        <Link
                          className='book-name-link'
                          to={`${basePath}/${bookId}`}
                        >
                          {getTitle(book)}
                        </Link>
                      </td>
                      <td>{currency.format(getPrice(book))}</td>
                      <td>
                        <span
                          className={`book-stock book-stock--${getStockState(stock)}`}
                        >
                          {stock}
                        </span>
                      </td>
                      <td>{currency.format(getStockValue(book))}</td>
                      <td>
                        <div className='book-actions'>
                          {canEditBook && (
                            <button
                              type='button'
                              onClick={() => openEditModal(book)}
                              disabled={loadingBookId === bookId}
                            >
                              {loadingBookId === bookId ? 'Loading...' : 'Edit'}
                            </button>
                          )}
                          <Link to={`${basePath}/${bookId}`}>View</Link>
                          {canDeleteBook && (
                            <button
                              className='book-actions__delete'
                              type='button'
                              onClick={() => setModal({ mode: 'delete', book })}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td className='book-table__empty' colSpan={columnCount}>
                    No books match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='book-pagination'>
          <span>Showing up to {BOOKS_PAGE_SIZE} books</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousCursor}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextCursor}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>
      {modal && (
        <BookModal
          mode={modal.mode}
          book={modal.book}
          authors={authorOptions}
          showAuthors={showAuthors}
          showIsbn={showIsbn}
          isSaving={isSaving}
          onClose={() => setModal(null)}
          onSave={saveBook}
        />
      )}
    </div>
  )
}

export default BookPublishe
